/**
 * عميل الواجهة البرمجية.
 *
 * يتولى ثلاثة أمور تتكرر في كل طلب: تحديد المستأجر، وإرفاق رمز الوصول،
 * وتجديده تلقائيًا عند انتهائه.
 */

const BASE = '/api';
/** جذر المسار — لطلبات fetch الخام كإطارات المراقبة الحيّة. */
export const API_BASE = BASE;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/* ==========================================================================
   تحديد المستأجر
   ========================================================================== */

const TENANT_OVERRIDE_KEY = 'goyone.tenant';

/**
 * في الإنتاج يُستنتج المستأجر من النطاق الفرعي ويتولاه الخادم من ترويسة
 * المضيف. أما على localhost فلا نطاق فرعي، فنسمح بتحديده صراحةً.
 *
 * الترتيب: النطاق الفرعي إن وُجد — فهو الحَكَم ولا يُتجاوز — ثم معامل الرابط،
 * ثم ما حُفظ سابقًا (وهذان للتطوير المحلّي).
 */
export function currentTenant(): string | null {
  const host = window.location.hostname;
  const parts = host.split('.');
  const reserved = ['www', 'api', 'app', 'admin', 'console', 'localhost'];
  if (parts.length >= 3 && !reserved.includes(parts[0]!)) return parts[0]!;

  const fromUrl = new URLSearchParams(window.location.search).get('tenant');
  if (fromUrl) {
    localStorage.setItem(TENANT_OVERRIDE_KEY, fromUrl);
    return fromUrl;
  }

  return localStorage.getItem(TENANT_OVERRIDE_KEY);
}

export function setTenant(slug: string): void {
  localStorage.setItem(TENANT_OVERRIDE_KEY, slug);
}

export function clearTenant(): void {
  localStorage.removeItem(TENANT_OVERRIDE_KEY);
}

/* ==========================================================================
   رمز الوصول
   ========================================================================== */

/*
 * الرمز في الذاكرة لا في localStorage.
 *
 * ما يُحفظ في localStorage يقرأه أي سكربت يعمل في الصفحة، فثغرة حقن واحدة
 * تكفي لسرقته. إبقاؤه في الذاكرة يعني ضياعه عند تحديث الصفحة، وهو ما تعالجه
 * كوكي التحديث المحجوبة عن جافاسكريبت: تُستبدل برمز جديد عند الإقلاع.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/* ==========================================================================
   الطلبات
   ========================================================================== */

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** لا يحاول التجديد عند 401 — يُستخدم في مسار التجديد نفسه لمنع التكرار. */
  noRetry?: boolean;
  /**
   * طلب عام لا يخص مستأجرًا ولا جلسة.
   *
   * صفحة الأسعار والتسجيل تُفتح قبل وجود حساب أصلًا. إرسال ترويسة مستأجر
   * محفوظة من زيارة سابقة يجعل الخادم يفسّر الطلب في نطاق شركة أخرى، ومحاولة
   * تجديد جلسة غير موجودة تُظهر خطأً لا معنى له للزائر.
   */
  skipAuth?: boolean;
  signal?: AbortSignal;
  /**
   * يُعاد الجسم كـBlob لا كـJSON.
   *
   * ملفات CSV تُنزَّل عبر fetch لا برابط مباشر: الطلب يحتاج ترويسة المستأجر
   * ورمز الوصول، وتنقّل المتصفح العادي لا يحملهما.
   */
  raw?: boolean;
}

async function raw<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};

  if (!opts.skipAuth) {
    const tenant = currentTenant();
    if (tenant) headers['X-Tenant'] = tenant;
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    // لإرسال كوكي التحديث مع مسارات المصادقة.
    credentials: 'include',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 204) return null as T;

  if (opts.raw) {
    if (!res.ok) {
      throw new ApiError(res.status, (await res.text()).slice(0, 200) || 'تعذّر التنزيل.');
    }
    return (await res.blob()) as T;
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      (payload as { message?: string } | null)?.message ??
      (typeof payload === 'string' && payload ? payload : 'تعذّر إتمام الطلب.');
    throw new ApiError(res.status, message);
  }

  return payload as T;
}

/* تمنع هذه المتغيرات إطلاق عدة عمليات تجديد متوازية عند انتهاء الرمز. */
let refreshing: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(fn: () => void): void {
  onSessionLost = fn;
}

/**
 * تجديد الجلسة، مع ضمان ألا تجري عمليتان معًا.
 *
 * رمز التحديث يُدوَّر: كل استخدام يُبطل السابق. فلو انطلق نداءان متزامنان،
 * دوّر الأول الرمز وفشل الثاني لأنه يحمل رمزًا صار ملغى، فتضيع الجلسة رغم
 * صحتها. لذلك يتشارك الجميع وعدًا واحدًا.
 *
 * هذا ليس احتمالًا نظريًا: React في وضع التطوير ينفّذ التأثيرات مرتين،
 * فيقع الاصطدام عند كل تحميل للصفحة ما لم تُوحَّد العملية هنا.
 */
export async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = raw<{ accessToken: string }>('/auth/refresh', {
      method: 'POST',
      noRetry: true,
    })
      .then((r) => {
        setAccessToken(r.accessToken);
        return true;
      })
      .catch(() => {
        setAccessToken(null);
        return false;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

/**
 * طلب مع تجديد تلقائي.
 *
 * عند انتهاء رمز الوصول يُجدَّد مرة واحدة ثم يُعاد الطلب. لو تعدّدت الطلبات
 * المتزامنة فكلها تنتظر عملية تجديد واحدة، فلا تتسابق على تدوير رمز التحديث
 * وتُبطل بعضها.
 */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  try {
    return await raw<T>(path, opts);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401 || opts.noRetry || opts.skipAuth) {
      throw err;
    }

    const ok = await refreshSession();
    if (!ok) {
      onSessionLost?.();
      throw err;
    }
    return raw<T>(path, { ...opts, noRetry: true });
  }
}

/**
 * رفع ملف بصيغة multipart.
 *
 * دالة مستقلة لا خيار في api(): الفرق ليس في المحتوى وحده بل في ترويسة
 * النوع — يجب ألّا تُضبط يدويًا، فالمتصفح هو من يكتبها مع حدّ الفصل الذي
 * يولّده. ضبطها يدويًا يُنتج طلبًا لا يستطيع الخادم تفكيكه.
 *
 * ولا تجديد تلقائي للرمز هنا: الجسم مقروء مرة واحدة، وإعادة الإرسال بعد
 * التجديد ترسل جسمًا مستهلكًا. من انتهت جلسته يعيد المحاولة بنفسه.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const tenant = currentTenant();
  if (tenant) headers['X-Tenant'] = tenant;
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  });

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      (payload as { message?: string } | null)?.message ??
      (typeof payload === 'string' && payload ? payload : 'تعذّر رفع الملف.');
    throw new ApiError(res.status, message);
  }

  return payload as T;
}

/** يبني سلسلة استعلام متجاهلًا القيم الفارغة. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}
